import React, { useState, useEffect, useCallback, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faTachometerAlt, 
  faCheckCircle, 
  faArrowLeft, 
  faDownload, 
  faShare, 
  faPrint,
  faEye,
  faEyeSlash,
  faTrophy,
  faBullseye,
  faChartLine,
  faFileAlt,
  faEnvelope,
  faUsers,
  faCog,
  faInfoCircle,
  faCalendarAlt,
  faHome,
  faDollarSign,
  faBed,
  faBath,
  faRuler,
  faCar,
  faSwimmingPool,
  faBrain,
  faRobot,
  faStar,
  faComments,
  faLightbulb,
  faQuoteLeft,
  faMessage,
  faPaperPlane,
  faSpinner,
      faChevronUp,
    faChevronDown,
    faMagic
} from '@fortawesome/free-solid-svg-icons';
  import { useWorkflow } from '../WorkflowProvider';
  import SmartCMACharts from 'components/smart-cma/SmartCMACharts';
  import axiosInstance from '../../../helpers/api';

// Add CSS styles for the chat component
const chatStyles = `
.ai-chat-panel {
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  border: 1px solid #e3e6f0;
}

.ai-chat-panel .card-header {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-bottom: none;
}

.chat-messages {
  background: #f8f9fc;
  border-radius: 0.35rem;
}

.chat-messages::-webkit-scrollbar {
  width: 6px;
}

.chat-messages::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 3px;
}

.chat-messages::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 3px;
}

.chat-messages::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}

.chat-message-user {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 18px 18px 4px 18px;
  box-shadow: 0 2px 4px rgba(102, 126, 234, 0.3);
}

.chat-message-assistant {
  background: #ffffff;
  border: 1px solid #e3e6f0;
  border-radius: 18px 18px 18px 4px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.suggested-question-btn {
  transition: all 0.2s ease;
  border-radius: 20px;
}

.suggested-question-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.chat-input-area {
  background: #ffffff;
  border-top: 2px solid #e3e6f0;
}

.chat-input-area textarea {
  border: 2px solid #e3e6f0;
  border-radius: 15px;
  transition: border-color 0.2s ease;
}

.chat-input-area textarea:focus {
  border-color: #667eea;
  box-shadow: 0 0 0 0.2rem rgba(102, 126, 234, 0.25);
}

.chat-send-btn {
  border-radius: 15px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border: none;
  transition: all 0.2s ease;
}

.chat-send-btn:hover {
  transform: scale(1.05);
  box-shadow: 0 4px 8px rgba(102, 126, 234, 0.3);
}

.quick-question-btn {
  background: #f8f9fc;
  border: 1px solid #e3e6f0;
  border-radius: 12px;
  transition: all 0.2s ease;
  font-size: 0.7rem;
}

.quick-question-btn:hover {
  background: #667eea;
  color: white;
  border-color: #667eea;
}

.token-counter {
  background: rgba(255, 255, 255, 0.2);
  border-radius: 12px;
  padding: 2px 8px;
  font-size: 0.75rem;
}
`;

// Inject styles
if (typeof document !== 'undefined') {
  const styleSheet = document.createElement("style");
  styleSheet.innerText = chatStyles;
  document.head.appendChild(styleSheet);
}

interface ComparableProperty {
  address?: string;
  bedrooms?: number;
  bathrooms?: number;
  living_area?: number;
  year_built?: number;
  close_price?: number;
  list_price?: number;
  price_per_sqft?: number;
  similarity_score?: number;
  city?: string;
  postal_code?: string;
  days_on_market?: number;
  // Allow for API field variations
  [key: string]: any;
}

interface DashboardData {
  executiveSummary: {
    recommendedPrice: number;
    confidence: number;
    marketPosition: 'conservative' | 'market' | 'aggressive';
    topInsights: string[];
    recommendedStrategy: string;
  };
  marketMetrics: {
    comparableCount: number;
    priceRange: { min: number; max: number; avg: number };
    daysOnMarket: number;
    pricePerSqft: number;
    marketVelocity: string;
  };
  actionRecommendations: {
    pricing: { conservative: number; market: number; aggressive: number };
    marketing: string[];
    timeline: string;
    nextSteps: string[];
  };
}

interface ClaudeInsights {
  marketNarrative: string;
  pricingRecommendation: string;
  marketPositioning: string;
  clientConsiderations: string;
  agentStrategy: string;
  riskAssessment: string;
  competitiveAdvantage: string;
  timingInsights: string;
  negotiationPoints: string;
  marketTrends: string;
  confidence: number;
  generatedAt: string;
}

interface MarketInsightsData {
  marketTemperature: {
    score: number; // 0-1 scale
    label: 'Hot' | 'Warm' | 'Balanced' | 'Cool' | 'Cold';
    indicators: {
      inventoryLevel: number;
      daysOnMarket: number;
      priceAppreciation: number;
      demandScore: number;
    };
  };
  competitiveAnalysis: {
    activeListings: number;
    recentSales: number;
    priceCompetitiveness: 'Above Market' | 'At Market' | 'Below Market';
    uniqueSellingPoints: string[];
  };
  seasonalTrends: {
    currentSeason: string;
    seasonalAdjustment: number;
    bestListingMonths: string[];
    marketVelocityTrend: 'Increasing' | 'Stable' | 'Decreasing';
  };
  riskFactors: {
    marketRisks: string[];
    propertyRisks: string[];
    timingRisks: string[];
    overallRiskScore: number; // 0-1 scale
  };
}

interface EnhancedDashboardData extends DashboardData {
  claudeInsights?: ClaudeInsights;
  xgboostPrediction?: {
    predicted_price: number;
    confidence: number;
    feature_importance: Array<{ feature: string; importance: number; display_name: string }>;
    model_version: string;
  };
  comparableAnalysis?: {
    comparables: ComparableProperty[];
    marketContext: any;
    searchMetrics: any;
  };
  marketInsights?: MarketInsightsData;
  leadId?: string;
}

interface AIChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: string;
  tokens?: number;
  cost?: number;
}

interface AIChatProps {
  reportData: EnhancedDashboardData;
  reportId: string;
  isExpanded: boolean;
  onToggle: () => void;
}

// Claude Insights Panel Component
const ClaudeInsightsPanel: React.FC<{ 
  insights: ClaudeInsights | null; 
  isLoading: boolean;
  onRegenerate: () => void;
  dashboardData?: EnhancedDashboardData | null;
}> = ({ insights, isLoading, onRegenerate, dashboardData }) => {
  const [activeTab, setActiveTab] = useState('narrative');
  
  if (isLoading) {
    return (
      <div className="card claude-insights-card mb-4">
        <div className="card-header bg-gradient-info text-white">
          <h3 className="mb-0">
            <FontAwesomeIcon icon={faBrain} className="me-3 fa-spin" />
            AI Market Intelligence
          </h3>
          <small>Claude is analyzing your market data...</small>
        </div>
        <div className="card-body text-center py-5">
          <div className="spinner-border text-info mb-3" role="status">
            <span className="visually-hidden">Generating insights...</span>
          </div>
          <h5 className="text-primary mb-2">🧠 AI Analysis in Progress</h5>
          <p className="text-muted mb-3">
            AI is processing your prediction, comparable data, and market trends to generate comprehensive insights.
          </p>
          <div className="bg-light p-3 rounded">
            <small className="text-muted">
              <strong>What&apos;s happening:</strong><br/>
              • Analyzing {sessionStorage.getItem('currentReportId')?.substring(0, 8)}... prediction results<br/>
              • Processing {dashboardData?.comparableAnalysis?.comparables?.length || 0} comparable properties and market data<br/>
              • Generating strategic recommendations using optimized prompts<br/>
              • Creating agent-specific insights with ~800-1000 tokens
            </small>
          </div>
          <div className="mt-3">
            <small className="text-muted">⏱️ Auto-started when you reached Step 4 • Typically takes 20-45 seconds</small>
          </div>
        </div>
      </div>
    );
  }

  if (!insights) {
    return (
      <div className="card claude-insights-card mb-4">
        <div className="card-header bg-warning text-dark">
          <h3 className="mb-0">
            <FontAwesomeIcon icon={faRobot} className="me-3" />
            AI Market Intelligence
          </h3>
          <small>Ready to generate AI insights</small>
        </div>
        <div className="card-body text-center py-4">
          <h5 className="text-muted mb-3">🤖 AI Insights Not Available</h5>
          <p className="text-muted mb-3">
            Claude AI insights are not ready yet. This could be because:
          </p>
          <ul className="text-muted text-start mb-4" style={{ maxWidth: '400px', margin: '0 auto' }}>
            <li>Background processing is still in progress</li>
            <li>Network connectivity issues occurred</li>
            <li>The insights generation was not triggered</li>
          </ul>
          <button className="btn btn-primary" onClick={onRegenerate}>
            <FontAwesomeIcon icon={faStar} className="me-2" />
            Generate AI Insights
          </button>
          <div className="mt-3">
            <small className="text-muted">
              <FontAwesomeIcon icon={faInfoCircle} className="me-1" />
              Your AI prediction and dashboard are fully functional without AI insights
            </small>
          </div>
        </div>
      </div>
    );
  }

  const insightTabs = [
    { id: 'narrative', label: 'Market Narrative', icon: faComments, content: insights.marketNarrative },
    { id: 'pricing', label: 'Pricing Strategy', icon: faDollarSign, content: insights.pricingRecommendation },
    { id: 'positioning', label: 'Market Position', icon: faBullseye, content: insights.marketPositioning },
    { id: 'strategy', label: 'Agent Strategy', icon: faLightbulb, content: insights.agentStrategy },
    { id: 'timing', label: 'Market Timing', icon: faCalendarAlt, content: insights.timingInsights },
    { id: 'risks', label: 'Risk Assessment', icon: faInfoCircle, content: insights.riskAssessment }
  ];

  return (
    <div className="card claude-insights-card mb-4">
      <div className="card-header bg-gradient-info text-white">
        <div className="d-flex justify-content-between align-items-center">
          <div>
            <h3 className="mb-0">
              <FontAwesomeIcon icon={faBrain} className="me-3" />
              AI Market Intelligence
            </h3>
            <small>
              <FontAwesomeIcon icon={faStar} className="me-2" />
              Powered by Claude 3.5 via AWS Bedrock • 
              {insights.confidence}% Confidence • 
              Generated {new Date(insights.generatedAt).toLocaleTimeString()}
            </small>
          </div>
          <button 
            className="btn btn-outline-light btn-sm"
            onClick={onRegenerate}
            title="Regenerate AI insights"
          >
            <FontAwesomeIcon icon={faStar} className="me-2" />
            Refresh
          </button>
        </div>
      </div>
      
      {/* Navigation Tabs */}
      <div className="card-header bg-light border-bottom-0">
        <ul className="nav nav-pills nav-justified">
          {insightTabs.map((tab) => (
            <li className="nav-item" key={tab.id}>
              <button
                className={`nav-link ${activeTab === tab.id ? 'active' : ''}`}
                onClick={() => setActiveTab(tab.id)}
              >
                <FontAwesomeIcon icon={tab.icon} className="me-2" />
                <span className="d-none d-md-inline">{tab.label}</span>
              </button>
            </li>
          ))}
        </ul>
      </div>
      
      {/* Active Tab Content */}
      <div className="card-body">
        <div className="claude-insight-content">
          <div className="insight-quote-wrapper">
            <FontAwesomeIcon icon={faQuoteLeft} className="quote-icon text-info" />
            <div className="insight-text">
              {insightTabs.find(tab => tab.id === activeTab)?.content}
            </div>
          </div>
          
          {/* Additional insights for specific tabs */}
          {activeTab === 'pricing' && insights.negotiationPoints && (
            <div className="mt-4 p-3 bg-light rounded">
              <h6 className="text-info mb-2">
                <FontAwesomeIcon icon={faComments} className="me-2" />
                Key Negotiation Points
              </h6>
              <div className="text-muted">{insights.negotiationPoints}</div>
            </div>
          )}
          
          {activeTab === 'positioning' && insights.competitiveAdvantage && (
            <div className="mt-4 p-3 bg-light rounded">
              <h6 className="text-success mb-2">
                <FontAwesomeIcon icon={faTrophy} className="me-2" />
                Competitive Advantages
              </h6>
              <div className="text-muted">{insights.competitiveAdvantage}</div>
            </div>
          )}
          
          {activeTab === 'strategy' && insights.clientConsiderations && (
            <div className="mt-4 p-3 bg-light rounded">
              <h6 className="text-primary mb-2">
                <FontAwesomeIcon icon={faUsers} className="me-2" />
                Client Considerations
              </h6>
              <div className="text-muted">{insights.clientConsiderations}</div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Enhanced Executive Summary with AI Integration
const EnhancedExecutiveSummary: React.FC<{ 
  summary: DashboardData['executiveSummary']; 
  xgboostPrediction?: any;
  claudeInsights?: ClaudeInsights | null;
}> = ({ summary, xgboostPrediction, claudeInsights }) => (
  <div className="card executive-summary-card mb-4">
    <div className="card-header bg-gradient-primary text-white">
      <h3 className="mb-0">
        <FontAwesomeIcon icon={faTrophy} className="me-3" />
        Executive Summary
      </h3>
      <small>Combining AI predictions with Claude AI market intelligence</small>
    </div>
    <div className="card-body">
      <div className="row">
        <div className="col-lg-6">
          <div className="summary-highlight text-center mb-4">
            <h2 className="price-recommendation text-primary mb-2">
              {formatPriceInK(summary.recommendedPrice)}
            </h2>
            <div className="confidence-indicators mb-3">
              <div className="mb-2">
                <span className="badge bg-success fs-6">
                  <FontAwesomeIcon icon={faBrain} className="me-2" />
                  AI: {xgboostPrediction?.confidence || summary.confidence}% Confidence
                </span>
              </div>
              {claudeInsights && (
                <div>
                  <span className="badge bg-info fs-6">
                    <FontAwesomeIcon icon={faRobot} className="me-2" />
                    Claude AI: {claudeInsights.confidence}% Confidence
                  </span>
                </div>
              )}
            </div>
            <p className="market-position text-muted">
              Market Position: <strong className="text-capitalize">{summary.marketPosition}</strong>
            </p>
          </div>
        </div>
        
        <div className="col-lg-6">
          <div className="key-insights">
            <h5 className="mb-3">
              <FontAwesomeIcon icon={faInfoCircle} className="me-2 text-info" />
              Key Market Insights
            </h5>
            
            {/* XGBoost Feature Importance */}
            {xgboostPrediction?.feature_importance && (
              <div className="mb-3">
                <h6 className="text-muted mb-2">Top Price Factors (AI Model):</h6>
                <ul className="list-unstyled">
                  {xgboostPrediction.feature_importance.slice(0, 3).map((feature: any, index: number) => (
                    <li key={index} className="mb-1">
                      <FontAwesomeIcon icon={faCheckCircle} className="me-2 text-success" />
                      {feature.display_name}: {(feature.importance * 100).toFixed(1)}%
                    </li>
                  ))}
                </ul>
              </div>
            )}
            
            {/* Traditional Insights */}
            <ul className="list-unstyled">
              {summary.topInsights.map((insight, index) => (
                <li key={index} className="mb-2">
                  <FontAwesomeIcon icon={faCheckCircle} className="me-2 text-success" />
                  {insight}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
      
      <div className="recommended-strategy mt-4 pt-4 border-top">
        <div className="row">
          <div className="col-md-6">
            <h5 className="mb-3">
              <FontAwesomeIcon icon={faChartLine} className="me-2 text-primary" />
              ML-Based Strategy
            </h5>
            <div className="strategy-card bg-light p-3 rounded">
              <p className="mb-0">{summary.recommendedStrategy}</p>
            </div>
          </div>
          
          {claudeInsights?.agentStrategy && (
            <div className="col-md-6">
              <h5 className="mb-3">
                <FontAwesomeIcon icon={faBrain} className="me-2 text-info" />
                AI Strategic Insights
              </h5>
              <div className="strategy-card bg-info bg-opacity-10 p-3 rounded">
                <p className="mb-0">{claudeInsights.agentStrategy.substring(0, 200)}...</p>
                <small className="text-muted">View full analysis in AI Intelligence panel</small>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  </div>
);



// Property Details Summary Component
const PropertyDetailsSummary: React.FC<{ property: any }> = ({ property }) => (
  <div className="card property-details-card mb-4">
    <div className="card-body py-3">
      <div className="d-flex justify-content-between align-items-center flex-wrap">
        <div className="d-flex align-items-center">
          <FontAwesomeIcon icon={faHome} className="me-2 text-primary" />
          <strong className="me-3">{property.property_address}</strong>
          <span className="text-muted me-3"><FontAwesomeIcon icon={faBed} className="me-1" />{property.bedrooms}bd</span>
          <span className="text-muted me-3"><FontAwesomeIcon icon={faBath} className="me-1" />{property.bathrooms}ba</span>
          <span className="text-muted me-3"><FontAwesomeIcon icon={faRuler} className="me-1" />{property.living_area?.toLocaleString()} sqft</span>
          <span className="text-muted me-3"><FontAwesomeIcon icon={faCalendarAlt} className="me-1" />Built {property.year_built}</span>
          {property.pool && <span className="text-muted"><FontAwesomeIcon icon={faSwimmingPool} className="me-1" />Pool</span>}
        </div>
        <span className="badge bg-success">Analysis Complete</span>
      </div>
    </div>
  </div>
);

// Agent Action Center Component
const AgentActionCenter: React.FC<{ actions: DashboardData['actionRecommendations'] }> = ({ actions }) => (
  <div className="card agent-action-center mb-4">
    <div className="card-header bg-gradient-warning text-dark">
      <h5 className="mb-0">
        <FontAwesomeIcon icon={faCog} className="me-2" />
        Agent Action Center
      </h5>
      <small>Pricing strategies and next steps</small>
    </div>
    <div className="card-body">
      <div className="pricing-strategies mb-4">
        <h6 className="mb-3">
          <FontAwesomeIcon icon={faDollarSign} className="me-2 text-success" />
          Pricing Strategy Options
        </h6>
        <div className="row">
          <div className="col-md-4">
            <div className="pricing-option text-center p-3 border rounded h-100 d-flex flex-column justify-content-between">
              <div>
                <h6 className="text-info mb-2">Conservative</h6>
                <h5 className="text-primary mb-0">{formatPriceInK(actions.pricing.conservative)}</h5>
              </div>
              <small className="text-muted mt-2">Quick sale priority</small>
            </div>
          </div>
          <div className="col-md-4">
            <div className="pricing-option text-center p-3 border rounded bg-light h-100 d-flex flex-column justify-content-between">
              <div>
                <h6 className="text-success mb-2">Market Value</h6>
                <h5 className="text-primary mb-0">{formatPriceInK(actions.pricing.market)}</h5>
              </div>
              <small className="text-muted mt-2"><strong>Recommended</strong></small>
            </div>
          </div>
          <div className="col-md-4">
            <div className="pricing-option text-center p-3 border rounded h-100 d-flex flex-column justify-content-between">
              <div>
                <h6 className="text-warning mb-2">Aggressive</h6>
                <h5 className="text-primary mb-0">{formatPriceInK(actions.pricing.aggressive)}</h5>
              </div>
              <small className="text-muted mt-2">Test market ceiling</small>
            </div>
          </div>
        </div>
      </div>
      
      <div className="marketing-recommendations mb-4">
        <h6 className="mb-3">
          <FontAwesomeIcon icon={faFileAlt} className="me-2 text-info" />
          Marketing Recommendations
        </h6>
        <ul className="list-unstyled">
          {actions.marketing.map((rec, index) => (
            <li key={index} className="mb-2">
              <FontAwesomeIcon icon={faCheckCircle} className="me-2 text-success" />
              {rec}
            </li>
          ))}
        </ul>
      </div>
      
      <div className="next-steps">
        <h6 className="mb-3">
          <FontAwesomeIcon icon={faCalendarAlt} className="me-2 text-warning" />
          Next Steps ({actions.timeline})
        </h6>
        <ul className="list-unstyled">
          {actions.nextSteps.map((step, index) => (
            <li key={index} className="mb-2">
              <FontAwesomeIcon icon={faCheckCircle} className="me-2 text-warning" />
              {step}
            </li>
          ))}
        </ul>
      </div>
    </div>
  </div>
);

// Report Export Panel Component
const ReportExportPanel: React.FC<{ 
  onExport: (format: string) => void;
  portalLink?: string;
  isGeneratingLink?: boolean;
}> = ({ onExport, portalLink, isGeneratingLink }) => (
  <div className="card report-export-panel">
    <div className="card-header">
      <h5 className="mb-0">
        <FontAwesomeIcon icon={faDownload} className="me-2" />
        Export & Share Report
      </h5>
    </div>
    <div className="card-body">
      <div className="export-options">
        {/* Portal Link Section */}
        {(portalLink || isGeneratingLink) && (
          <div className="mb-3 p-3 bg-light rounded">
            <h6 className="text-primary mb-2">
              <FontAwesomeIcon icon={faShare} className="me-2" />
              Seller Portal Link
            </h6>
            {isGeneratingLink ? (
              <div className="text-center">
                <FontAwesomeIcon icon={faSpinner} spin className="me-2" />
                Generating secure portal link...
              </div>
            ) : portalLink ? (
              <div>
                <div className="input-group mb-2">
                  <input 
                    type="text" 
                    className="form-control form-control-sm" 
                    value={portalLink} 
                    readOnly 
                  />
                  <button 
                    className="btn btn-sm btn-outline-secondary" 
                    onClick={() => {
                      navigator.clipboard.writeText(portalLink);
                      alert('Portal link copied to clipboard!');
                    }}
                  >
                    Copy
                  </button>
                </div>
                <small className="text-muted">
                  Share this link with your seller for automated weekly updates
                </small>
              </div>
            ) : null}
          </div>
        )}
        
        <div className="row g-2">
          <div className="col-md-6">
            <button 
              className="btn btn-primary w-100"
              onClick={() => onExport('pdf')}
            >
              <FontAwesomeIcon icon={faDownload} className="me-2" />
              Download PDF Report
            </button>
          </div>
          <div className="col-md-6">
            <button 
              className="btn btn-outline-primary w-100"
              onClick={() => onExport('email')}
            >
              <FontAwesomeIcon icon={faEnvelope} className="me-2" />
              Email to Client
            </button>
          </div>
        </div>
        
        <div className="row g-2 mt-2">
          <div className="col-md-6">
            <button 
              className="btn btn-outline-secondary w-100"
              onClick={() => onExport('print')}
            >
              <FontAwesomeIcon icon={faPrint} className="me-2" />
              Print Report
            </button>
          </div>
          <div className="col-md-6">
            <button 
              className="btn btn-success w-100"
              onClick={() => onExport('portal')}
            >
              <FontAwesomeIcon icon={faMagic} className="me-2" />
              Generate Portal Link
            </button>
          </div>
        </div>
      </div>
      
      <div className="mt-3 pt-3 border-top">
        <small className="text-muted">
          <FontAwesomeIcon icon={faInfoCircle} className="me-1" />
          All reports include KairosIQ branding and professional formatting
        </small>
      </div>
    </div>
  </div>
);

// Enhanced Market Temperature Component
const MarketTemperatureGauge: React.FC<{ 
  temperature: MarketInsightsData['marketTemperature'];
  size?: 'sm' | 'md' | 'lg';
}> = ({ temperature, size = 'md' }) => {
  const getTemperatureColor = (score: number) => {
    if (score >= 0.8) return '#dc3545'; // Hot - Red
    if (score >= 0.6) return '#fd7e14'; // Warm - Orange  
    if (score >= 0.4) return '#ffc107'; // Balanced - Yellow
    if (score >= 0.2) return '#20c997'; // Cool - Teal
    return '#6f42c1'; // Cold - Purple
  };

  const gaugeSize = size === 'sm' ? 120 : size === 'lg' ? 200 : 160;
  const strokeWidth = size === 'sm' ? 8 : size === 'lg' ? 12 : 10;
  
  return (
    <div className="market-temperature-gauge text-center">
      <div className="position-relative d-inline-block">
        <svg width={gaugeSize} height={gaugeSize/2 + 20}>
          {/* Background arc */}
          <path
            d={`M 20 ${gaugeSize/2} A ${gaugeSize/2-20} ${gaugeSize/2-20} 0 0 1 ${gaugeSize-20} ${gaugeSize/2}`}
            fill="none"
            stroke="#e9ecef"
            strokeWidth={strokeWidth}
          />
          {/* Temperature arc */}
          <path
            d={`M 20 ${gaugeSize/2} A ${gaugeSize/2-20} ${gaugeSize/2-20} 0 0 1 ${20 + (gaugeSize-40) * temperature.score} ${gaugeSize/2 - (gaugeSize/2-20) * Math.sin(Math.PI * temperature.score)}`}
            fill="none"
            stroke={getTemperatureColor(temperature.score)}
            strokeWidth={strokeWidth}
            strokeLinecap="round"
          />
          {/* Center text */}
          <text x={gaugeSize/2} y={gaugeSize/2 + 5} textAnchor="middle" className="fw-bold" fontSize={size === 'sm' ? '12' : '16'}>
            {temperature.label}
          </text>
        </svg>
      </div>
      <div className="mt-2">
        <small className="text-muted">Market Temperature</small>
        <div className="d-flex justify-content-around mt-2 text-center">
          <div>
            <small className="d-block text-muted">Inventory</small>
            <small className="fw-bold">{temperature.indicators.inventoryLevel}</small>
          </div>
          <div>
            <small className="d-block text-muted">Avg DOM</small>
            <small className="fw-bold">{temperature.indicators.daysOnMarket}d</small>
          </div>
          <div>
            <small className="d-block text-muted">Appreciation</small>
            <small className="fw-bold">{(temperature.indicators.priceAppreciation * 100).toFixed(1)}%</small>
          </div>
        </div>
      </div>
    </div>
  );
};

// Enhanced Competitive Analysis Component
const CompetitiveAnalysisPanel: React.FC<{ 
  analysis: MarketInsightsData['competitiveAnalysis'];
  propertyPrice: number; // eslint-disable-line no-unused-vars
}> = ({ analysis, propertyPrice }) => ( // eslint-disable-line no-unused-vars
  <div className="card competitive-analysis-panel mb-4">
    <div className="card-header bg-gradient-success text-white">
      <h5 className="mb-0">
        <FontAwesomeIcon icon={faBullseye} className="me-2" />
        Competitive Market Position
      </h5>
    </div>
    <div className="card-body">
      <div className="row">
        <div className="col-md-4">
          <div className="competitive-metric text-center p-3 border rounded">
            <h4 className="text-primary mb-1">{analysis.activeListings}</h4>
            <small className="text-muted">Active Listings</small>
          </div>
        </div>
        <div className="col-md-4">
          <div className="competitive-metric text-center p-3 border rounded">
            <h4 className="text-success mb-1">{analysis.recentSales}</h4>
            <small className="text-muted">Recent Sales (90d)</small>
          </div>
        </div>
        <div className="col-md-4">
          <div className="competitive-metric text-center p-3 border rounded">
            <span className={`badge fs-6 ${
              analysis.priceCompetitiveness === 'At Market' ? 'bg-success' :
              analysis.priceCompetitiveness === 'Below Market' ? 'bg-info' : 'bg-warning'
            }`}>
              {analysis.priceCompetitiveness}
            </span>
            <small className="d-block text-muted mt-1">Price Position</small>
          </div>
        </div>
      </div>
      
      <div className="mt-4">
        <h6 className="text-primary mb-3">
          <FontAwesomeIcon icon={faTrophy} className="me-2" />
          Unique Selling Points
        </h6>
        <div className="row">
          {analysis.uniqueSellingPoints.map((point, index) => (
            <div key={index} className="col-md-6 mb-2">
              <div className="d-flex align-items-center">
                <FontAwesomeIcon icon={faCheckCircle} className="me-2 text-success" />
                <span className="small">{point}</span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  </div>
);

// Enhanced Risk Assessment Component  
const RiskAssessmentPanel: React.FC<{ 
  riskFactors: MarketInsightsData['riskFactors'];
}> = ({ riskFactors }) => {
  const getRiskColor = (score: number) => {
    if (score >= 0.7) return 'danger';
    if (score >= 0.4) return 'warning'; 
    return 'success';
  };

  const getRiskLabel = (score: number) => {
    if (score >= 0.7) return 'High Risk';
    if (score >= 0.4) return 'Moderate Risk';
    return 'Low Risk';
  };

  return (
    <div className="card risk-assessment-panel mb-4">
      <div className="card-header bg-gradient-warning text-dark">
        <div className="d-flex justify-content-between align-items-center">
          <h5 className="mb-0">
            <FontAwesomeIcon icon={faInfoCircle} className="me-2" />
            Risk Assessment
          </h5>
          <span className={`badge bg-${getRiskColor(riskFactors.overallRiskScore)} fs-6`}>
            {getRiskLabel(riskFactors.overallRiskScore)}
          </span>
        </div>
      </div>
      <div className="card-body">
        <div className="row">
          <div className="col-md-4">
            <h6 className="text-danger mb-3">Market Risks</h6>
            <ul className="list-unstyled">
              {riskFactors.marketRisks.map((risk, index) => (
                <li key={index} className="mb-2">
                  <FontAwesomeIcon icon={faInfoCircle} className="me-2 text-danger" />
                  <small>{risk}</small>
                </li>
              ))}
            </ul>
          </div>
          <div className="col-md-4">
            <h6 className="text-warning mb-3">Property Risks</h6>
            <ul className="list-unstyled">
              {riskFactors.propertyRisks.map((risk, index) => (
                <li key={index} className="mb-2">
                  <FontAwesomeIcon icon={faHome} className="me-2 text-warning" />
                  <small>{risk}</small>
                </li>
              ))}
            </ul>
          </div>
          <div className="col-md-4">
            <h6 className="text-info mb-3">Timing Risks</h6>
            <ul className="list-unstyled">
              {riskFactors.timingRisks.map((risk, index) => (
                <li key={index} className="mb-2">
                  <FontAwesomeIcon icon={faCalendarAlt} className="me-2 text-info" />
                  <small>{risk}</small>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

// Enhanced Market Insights Dashboard
const MarketInsightsDashboard: React.FC<{ 
  insights: MarketInsightsData;
  propertyPrice: number;
}> = ({ insights, propertyPrice }) => (
  <div className="market-insights-dashboard mb-4">
    <div className="row">
      <div className="col-lg-4">
        <div className="card h-100">
          <div className="card-body text-center">
            <MarketTemperatureGauge temperature={insights.marketTemperature} size="lg" />
          </div>
        </div>
      </div>
      <div className="col-lg-8">
        <div className="card h-100">
          <div className="card-header">
            <h5 className="mb-0">
              <FontAwesomeIcon icon={faCalendarAlt} className="me-2" />
              Seasonal Market Trends
            </h5>
          </div>
          <div className="card-body">
            <div className="row">
              <div className="col-md-6">
                <div className="seasonal-info">
                  <h6 className="text-primary">Current Season Impact</h6>
                  <p className="mb-2">
                    <strong>{insights.seasonalTrends.currentSeason}</strong> season with{' '}
                    <span className={`fw-bold ${insights.seasonalTrends.seasonalAdjustment > 0 ? 'text-success' : 'text-danger'}`}>
                      {insights.seasonalTrends.seasonalAdjustment > 0 ? '+' : ''}
                      {(insights.seasonalTrends.seasonalAdjustment * 100).toFixed(1)}%
                    </span>{' '}
                    seasonal adjustment
                  </p>
                  <small className="text-muted">
                    Market velocity is {insights.seasonalTrends.marketVelocityTrend.toLowerCase()}
                  </small>
                </div>
              </div>
              <div className="col-md-6">
                <div className="optimal-timing">
                  <h6 className="text-success">Optimal Listing Months</h6>
                  <div className="d-flex flex-wrap gap-1">
                    {insights.seasonalTrends.bestListingMonths.map((month, index) => (
                      <span key={index} className="badge bg-success">{month}</span>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    
    <CompetitiveAnalysisPanel 
      analysis={insights.competitiveAnalysis} 
      propertyPrice={propertyPrice}
    />
    
    <RiskAssessmentPanel riskFactors={insights.riskFactors} />
  </div>
);

// Helper function to format price in thousands
const formatPriceInK = (price: number): string => {
  if (price >= 1000000) {
    return `$${(price / 1000000).toFixed(1)}M`;
  } else if (price >= 1000) {
    return `$${Math.round(price / 1000)}K`;
  } else {
    return `$${price}`;
  }
};

// AI Chat Component
const AIChatPanel: React.FC<AIChatProps> = ({ reportData, reportId, isExpanded, onToggle }) => {
  const [messages, setMessages] = useState<AIChatMessage[]>([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [totalTokens, setTotalTokens] = useState(0);
  const [totalCost, setTotalCost] = useState(0);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Suggested questions based on report data
  const suggestedQuestions = [
    "Why is this the recommended price for this property?",
    "Compare this property to the top 3 comparable sales in detail",
    "What are the risks if we price 5% above the AI estimate?",
    "Create a listing strategy for this property",
    "What neighborhood trends are affecting this valuation?",
    "Help me prepare talking points for the listing appointment"
  ];

  const sendMessage = async (message: string) => {
    if (!message.trim() || isLoading) return;

    const userMessage: AIChatMessage = {
      id: Date.now().toString(),
      role: 'user',
      content: message,
      timestamp: new Date().toISOString()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputMessage('');
    setIsLoading(true);

    try {
      // Build CMA context from sessionStorage seller report data
      const sellerReportStr = sessionStorage.getItem('fullCMAResponse') || sessionStorage.getItem('sellerReportComps');
      let cmaContext: any = {};
      if (sellerReportStr) {
        try {
          const parsed = JSON.parse(sellerReportStr);
          cmaContext = {
            property: parsed.subject_property,
            valuation: {
              estimated_value: parsed.ml_prediction?.predicted_price || parsed.prediction?.value || 0,
              confidence_score: parsed.ml_prediction?.confidence_score || parsed.prediction?.confidence_score || 0,
              mape_accuracy: parsed.ml_prediction?.mape_accuracy || 89
            },
            comparables: parsed.comparable_properties?.comparables || [],
            comparable_count: parsed.comparable_properties?.total_found || 0,
            market_stats: parsed.market_stats || parsed.comparable_stats || {}
          };
        } catch (e) {}
      }

      // Build detailed comp breakdown for AI context
      const compBreakdown = (cmaContext.comparables || []).map((c: any, i: number) =>
        `Comp ${i+1}: ${c.address} | Sold: $${(c.close_price || 0).toLocaleString()} | ${c.bedrooms}bd/${c.bathrooms}ba | ${(c.living_area || 0).toLocaleString()}sqft | $${c.price_per_sqft || Math.round((c.close_price || 0) / (c.living_area || 1))}/sqft | ${c.classification === 'renovated' ? 'RENOVATED' : 'Original condition'} | ${c.days_on_market || 0} DOM | ${c.distance_miles || 0}mi away`
      ).join('\n');

      const subjectProp = cmaContext.property || {};
      const systemPrompt = `You are an expert real estate CMA analyst helping an agent prepare for a listing presentation. You have detailed knowledge of this property and all comparable sales.

SUBJECT PROPERTY:
- Address: ${subjectProp.address || 'Unknown'}
- ${subjectProp.bedrooms || 3}bd/${subjectProp.bathrooms || 2}ba, ${subjectProp.living_area || subjectProp.sqft || 0} sqft
- Year Built: ${subjectProp.year_built || subjectProp.yearBuilt || 'Unknown'}

AI VALUATION: $${(cmaContext.valuation?.estimated_value || 0).toLocaleString()}
Prediction Accuracy: ${Math.round(cmaContext.valuation?.mape_accuracy || 89)}%
Price Range: $${Math.round((cmaContext.valuation?.estimated_value || 0) * 0.95).toLocaleString()} - $${Math.round((cmaContext.valuation?.estimated_value || 0) * 1.05).toLocaleString()}

COMPARABLE SALES (${cmaContext.comparables?.length || 0} properties):
${compBreakdown || 'No comparable data available'}

MARKET STATS:
- Median Price: $${(cmaContext.market_stats?.median_price || cmaContext.market_stats?.price_median || 0).toLocaleString()}
- Avg $/sqft: $${cmaContext.market_stats?.median_ppsf || cmaContext.market_stats?.psf_avg || 0}
- Avg Days on Market: ${cmaContext.market_stats?.avg_days_on_market || cmaContext.market_stats?.avg_dom || 30}

Instructions: Reference specific comps by address and price when answering. Compare the subject property features to comps. Provide data-driven recommendations. Be concise and professional. When discussing listing descriptions, use the actual property details.`;

      const response = await axiosInstance.post('/public/ai/chat', {
        messages: [
          { role: 'system', content: systemPrompt },
          ...messages.slice(-10).map(m => ({ role: m.role, content: m.content })),
          { role: 'user', content: message }
        ],
        sessionId: `cma_step4_${Date.now()}`,
        context: {
          type: 'seller_cma',
          cmaData: cmaContext
        }
      });

      const assistantMessage: AIChatMessage = {
        id: (Date.now() + 1).toString(),
        role: 'assistant',
        content: response.data.response || response.data.message || 'No response received.',
        timestamp: new Date().toISOString(),
        tokens: response.data.tokens,
        cost: response.data.cost
      };

      setMessages(prev => [...prev, assistantMessage]);
      
      // Update token tracking
      if (response.data.tokens) {
        setTotalTokens(prev => prev + response.data.tokens);
      }
      if (response.data.cost) {
        setTotalCost(prev => prev + response.data.cost);
      }

    } catch (error) {
      console.error('❌ [AI CHAT] Error sending message:', error);
      const errorMessage: AIChatMessage = {
        id: (Date.now() + 1).toString(),
        role: 'assistant',
        content: 'I apologize, but I encountered an error processing your request. Please try again or contact support if the issue persists.',
        timestamp: new Date().toISOString()
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSuggestedQuestion = (question: string) => {
    sendMessage(question);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage(inputMessage);
    }
  };

  return (
    <div className="card ai-chat-panel mb-4">
      <div className="card-header bg-gradient-primary text-white">
        <div className="d-flex justify-content-between align-items-center">
          <div>
            <h3 className="mb-0">
              <FontAwesomeIcon icon={faMessage} className="me-3" />
              AI Chat Assistant
            </h3>
            <small>
              <FontAwesomeIcon icon={faMagic} className="me-2" />
              Ask detailed questions about this CMA report
                             {totalTokens > 0 && (
                 <span className="ms-3 token-counter">
                   • {totalTokens.toLocaleString()} tokens used
                   {totalCost > 0 && ` • $${totalCost.toFixed(4)} cost`}
                 </span>
               )}
            </small>
          </div>
          <button 
            className="btn btn-outline-light btn-sm"
            onClick={onToggle}
            title={isExpanded ? "Minimize chat" : "Expand chat"}
          >
            <FontAwesomeIcon icon={isExpanded ? faChevronUp : faChevronDown} className="me-2" />
            {isExpanded ? 'Minimize' : 'Expand'}
          </button>
        </div>
      </div>

      {isExpanded && (
        <div className="card-body p-0">
          {/* Chat Messages */}
          <div className="chat-messages" style={{ height: '600px', overflowY: 'auto', padding: '1rem' }}>
            {messages.length === 0 ? (
              <div className="text-center text-muted py-4">
                <FontAwesomeIcon icon={faComments} size="3x" className="mb-3 text-primary opacity-50" />
                <h5>Start a conversation about this CMA report</h5>
                <p>I have access to all the property data, comparable sales, ML predictions, and market analysis.</p>
                
                {/* Suggested Questions */}
                <div className="mt-4">
                  <h6 className="text-primary mb-3">Suggested Questions:</h6>
                  <div className="d-flex flex-wrap gap-2 justify-content-center">
                    {suggestedQuestions.slice(0, 3).map((question, index) => (
                                             <button
                         key={index}
                         className="btn btn-outline-primary btn-sm suggested-question-btn"
                         onClick={() => handleSuggestedQuestion(question)}
                         style={{ fontSize: '0.75rem' }}
                       >
                        {question}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            ) : (
              <div>
                {messages.map((message) => (
                  <div
                    key={message.id}
                    className={`mb-3 d-flex ${message.role === 'user' ? 'justify-content-end' : 'justify-content-start'}`}
                  >
                                         <div
                       className={`p-3 ${
                         message.role === 'user'
                           ? 'chat-message-user text-white'
                           : 'chat-message-assistant text-dark'
                       }`}
                       style={{ maxWidth: '80%' }}
                     >
                      <div className="mb-1">
                        <strong>
                          {message.role === 'user' ? 'You' : 'AI Assistant'}
                        </strong>
                        <small className="ms-2 opacity-75">
                          {new Date(message.timestamp).toLocaleTimeString()}
                        </small>
                      </div>
                      <div style={{ whiteSpace: 'pre-wrap' }}>
                        {message.content}
                      </div>
                      {message.tokens && (
                        <small className="d-block mt-1 opacity-75">
                          {message.tokens} tokens
                          {message.cost && ` • $${message.cost.toFixed(4)}`}
                        </small>
                      )}
                    </div>
                  </div>
                ))}
                {isLoading && (
                  <div className="mb-3 d-flex justify-content-start">
                    <div className="bg-light text-dark p-3 rounded" style={{ maxWidth: '80%' }}>
                      <FontAwesomeIcon icon={faSpinner} spin className="me-2" />
                      AI is thinking...
                    </div>
                  </div>
                )}
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

                     {/* Chat Input */}
           <div className="chat-input-area p-3">
            <div className="input-group">
              <textarea
                className="form-control"
                placeholder="Ask me anything about this CMA report..."
                value={inputMessage}
                onChange={(e) => setInputMessage(e.target.value)}
                onKeyPress={handleKeyPress}
                rows={2}
                disabled={isLoading}
                style={{ resize: 'none' }}
              />
                             <button
                 className="btn btn-primary chat-send-btn"
                 onClick={() => sendMessage(inputMessage)}
                 disabled={isLoading || !inputMessage.trim()}
               >
                <FontAwesomeIcon icon={isLoading ? faSpinner : faPaperPlane} spin={isLoading} />
              </button>
            </div>
            
            {/* Quick Actions */}
            {messages.length > 0 && (
              <div className="mt-2">
                <small className="text-muted">Quick questions:</small>
                <div className="d-flex flex-wrap gap-1 mt-1">
                  {suggestedQuestions.slice(3, 6).map((question, index) => (
                                         <button
                       key={index}
                       className="btn btn-outline-secondary btn-sm quick-question-btn"
                       onClick={() => handleSuggestedQuestion(question)}
                       style={{ fontSize: '0.7rem' }}
                       disabled={isLoading}
                     >
                      {question.length > 30 ? question.substring(0, 30) + '...' : question}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const Step4Dashboard: React.FC = () => {
  const { state, goToPreviousStep } = useWorkflow();
  const [dashboardData, setDashboardData] = useState<EnhancedDashboardData | null>(null);
  const [claudeInsights, setClaudeInsights] = useState<ClaudeInsights | null>(null);
  const [isLoadingInsights, setIsLoadingInsights] = useState(false);
  const [insightsRequested, setInsightsRequested] = useState(false);
  const [presentationMode, setPresentationMode] = useState(false);
  const [isChatExpanded, setIsChatExpanded] = useState(false);

  // Generate Claude insights from consolidated data
  const generateClaudeInsights = useCallback(async () => {
    // Prevent duplicate requests
    if (isLoadingInsights || insightsRequested) {
      console.log('🚫 [CLAUDE INSIGHTS] Request already in progress or completed, skipping...');
      return;
    }

    // Get the current report ID
    const currentReportId = sessionStorage.getItem('currentReportId');
    if (!currentReportId) {
      console.warn('🧠 [CLAUDE INSIGHTS] No report ID found, cannot generate insights');
      return;
    }

    // Skip old insights generation for seller reports - handled on mount
    const sellerCheck = sessionStorage.getItem('sellerReportComps');
    if (sellerCheck) {
      console.log('✅ [CLAUDE INSIGHTS] Seller report - insights handled on mount, skipping');
      setIsLoadingInsights(false);
      return;
    }

    setIsLoadingInsights(true);
    setInsightsRequested(true);

    try {
      console.log('🧠 [CLAUDE INSIGHTS] Starting insights generation for report:', currentReportId);
      
      const consolidatedData = {
        propertyData: state.propertyInput,
        mlsData: state.mlsData,
        xgboostPrediction: dashboardData?.xgboostPrediction,
        comparableAnalysis: dashboardData?.comparableAnalysis,
        marketMetrics: dashboardData?.marketMetrics,
        timestamp: new Date().toISOString()
      };

      // Skip old smart-cma insights API for seller reports - insights generated on mount
      const sellerCheck = sessionStorage.getItem('sellerReportComps');
      if (sellerCheck) {
        console.log('✅ [CLAUDE INSIGHTS] Seller report - insights handled on mount');
        setIsLoadingInsights(false);
        return;
      }
      const response = await axiosInstance.post('/smart-cma/generate-claude-insights', {
        reportId: currentReportId,
        consolidatedData,
        insightTypes: [
          'market_narrative',
          'pricing_recommendation', 
          'market_positioning',
          'agent_strategy',
          'timing_insights',
          'risk_assessment',
          'competitive_advantage',
          'client_considerations',
          'negotiation_points',
          'market_trends'
        ],
        temperature: 0.7,
        maxTokens: 4000
      });

      // Axios automatically handles HTTP errors, so if we get here, the request was successful
      const insights = response.data;
      console.log('✅ [CLAUDE INSIGHTS] Insights generation started:', insights);
      
      // Check if this is an immediate response or async processing
      if (insights.status === 'processing') {
        console.log('🔄 [CLAUDE INSIGHTS] Background processing started, will poll for results');
        // Start polling for completion
        startClaudePolling();
      } else {
        // Direct response (cached or immediate)
        setClaudeInsights(insights);
        console.log('✅ [CLAUDE INSIGHTS] Successfully received insights');
      }
    } catch (error) {
      console.error('❌ [CLAUDE INSIGHTS] Error generating insights:', error);
      // Reset flags on network errors to allow manual retry
      setInsightsRequested(false);
    } finally {
      // Don't set loading to false here if we're polling
    }
  }, [state.propertyInput, state.mlsData, dashboardData?.xgboostPrediction, dashboardData?.comparableAnalysis, dashboardData?.marketMetrics, isLoadingInsights, insightsRequested]);

  const compileDashboardData = useCallback(async () => {
    try {
      console.log('🔧 [STEP 4] Compiling dashboard data with state:', {
        propertyInput: !!state.propertyInput,
        mlsData: !!state.mlsData,
        predictionResult: !!state.predictionResult
      });

      // Get the current report ID - try multiple sources
      let currentReportId = sessionStorage.getItem('currentReportId') || 
                           localStorage.getItem('currentReportId');
      
      // Use whatever report ID is available
      if (!currentReportId) {
        console.log('⚠️ [STEP 4] No report ID in storage');
      }
      
      console.log('🔍 [STEP 4] Report ID sources:', {
        sessionStorage: sessionStorage.getItem('currentReportId'),
        localStorage: localStorage.getItem('currentReportId'),
        forced: currentReportId,
        final: currentReportId
      });
      
      if (!currentReportId) {
        console.warn('⚠️ [STEP 4] No report ID found, using fallback data');
      }

      // Get property data from Step 1 with proper typing
      const propertyData = state.propertyInput as any || {};
      console.log('📊 [STEP 4] Property data:', propertyData);

      // Fetch comparable data - try sessionStorage first (seller reports), then API
      let comparables: ComparableProperty[] = [];
      let marketContext = {} as any;
      let reportData = null;
      let fetchSuccess = false;

      // Check for pre-loaded seller report data first
      const sellerData = sessionStorage.getItem('sellerReportComps') || sessionStorage.getItem('fullCMAResponse');
      if (sellerData) {
        try {
          const parsed = JSON.parse(sellerData);
          const comps = parsed.comparable_properties?.comparables || [];
          if (comps.length > 0) {
            comparables = comps;
            fetchSuccess = true;
            console.log(`✅ [STEP 4] Using ${comps.length} comps from seller report sessionStorage`);

            // Build market context from seller data (field names must match what compileDashboardData expects)
            const prices = comps.map((c: any) => c.close_price || 0).filter((p: number) => p > 0);
            const ppsf = comps.map((c: any) => c.price_per_sqft || (c.close_price && c.living_area ? c.close_price / c.living_area : 0)).filter((p: number) => p > 0);
            const doms = comps.map((c: any) => c.days_on_market || 0).filter((d: number) => d > 0);
            marketContext = {
              avg_sale_price: prices.length ? Math.round(prices.reduce((a: number, b: number) => a + b, 0) / prices.length) : 0,
              avg_price_per_sqft: ppsf.length ? Math.round(ppsf.reduce((a: number, b: number) => a + b, 0) / ppsf.length) : 0,
              avg_days_on_market: doms.length ? Math.round(doms.reduce((a: number, b: number) => a + b, 0) / doms.length) : 30,
              total_found: comps.length,
              medianPrice: parsed.market_stats?.median_price || parsed.comparable_stats?.price_median || 0
            };
            // Set reportData for downstream use
            reportData = { comparableCount: comps.length };
          }
        } catch (e) {
          console.warn('⚠️ [STEP 4] Could not parse seller report data');
        }
      }

      if (!fetchSuccess && currentReportId) {
        try {
          console.log('🔄 [STEP 4] Fetching report data from database...');
          const response = await axiosInstance.get(`/smart-cma/step2/get-report/${currentReportId}`, { timeout: 10000 });
          
          console.log('📊 [STEP 4] API Response status:', response.status);
          console.log('📊 [STEP 4] API Response data keys:', Object.keys(response.data));
          
          if (response.data.success && response.data.data) {
            reportData = response.data.data;
            console.log('🔍 [STEP 4 DEBUG] === FULL API RESPONSE ===');
            console.log('📊 [STEP 4] Complete reportData object:', JSON.stringify(reportData, null, 2));
            console.log('📊 [STEP 4] Report data keys:', Object.keys(reportData));
            console.log('📊 [STEP 4] Direct comparableCount check:', reportData.comparableCount);
            console.log('📊 [STEP 4] Nested data.comparableCount check:', reportData.data?.comparableCount);
            console.log('📊 [STEP 4] Comparables data structure:', reportData.comparablesData ? Object.keys(reportData.comparablesData) : 'No comparablesData');
            console.log('🔍 [STEP 4 DEBUG] === END API RESPONSE ===');
            
            // FIXED: Handle multiple possible data structures based on API response
            const comparablesData = reportData.comparablesData || {};
            
            // Priority 1: Check for comparable_properties direct array (WHAT THE API ACTUALLY RETURNS)
            if (comparablesData.comparable_properties && Array.isArray(comparablesData.comparable_properties)) {
              comparables = comparablesData.comparable_properties;
              console.log('✅ [STEP 4] Found', comparables.length, 'comparables in comparable_properties direct array');
            }
            // Priority 2: Check for mls_listings (direct response format)
            else if (comparablesData.mls_listings && Array.isArray(comparablesData.mls_listings)) {
              comparables = comparablesData.mls_listings;
              console.log('✅ [STEP 4] Found', comparables.length, 'comparables in mls_listings');
            }
            // Priority 3: Check for enhanced_comparables
            else if (comparablesData.enhanced_comparables && Array.isArray(comparablesData.enhanced_comparables)) {
              comparables = comparablesData.enhanced_comparables;
              console.log('✅ [STEP 4] Found', comparables.length, 'comparables in enhanced_comparables');
            }
            // Priority 4: Check for direct comparables array
            else if (comparablesData.comparables && Array.isArray(comparablesData.comparables)) {
              comparables = comparablesData.comparables;
              console.log('✅ [STEP 4] Found', comparables.length, 'comparables in direct comparables array');
            }
            // Priority 5: Check for comparable_properties.comparables
            else if (comparablesData.comparable_properties?.comparables && Array.isArray(comparablesData.comparable_properties.comparables)) {
              comparables = comparablesData.comparable_properties.comparables;
              console.log('✅ [STEP 4] Found', comparables.length, 'comparables in comparable_properties.comparables');
            }
            else {
              console.warn('⚠️ [STEP 4] No comparables found in expected locations');
              console.log('📊 [STEP 4] Available comparablesData keys:', Object.keys(comparablesData));
            }
            
            // ENHANCED: Map API field names and calculate market context from actual comparable data
            if (comparables.length > 0) {
              console.log('🔍 [STEP 4] Sample comparable structure:', comparables[0]);
              
              // Map API fields to frontend expected format
              const mappedComparables = comparables.map(comp => ({
                ...comp,
                // Map PascalCase API fields to snake_case frontend fields
                close_price: comp.close_price || (comp as any).ClosePrice,
                list_price: comp.list_price || (comp as any).ListPrice,
                living_area: comp.living_area || (comp as any).LivingArea,
                bedrooms: comp.bedrooms || (comp as any).BedroomsTotal,
                bathrooms: comp.bathrooms || (comp as any).BathsTotal,
                year_built: comp.year_built || (comp as any).YearBuilt,
                address: comp.address || (comp as any).UnparsedAddress,
                city: comp.city || (comp as any).City,
                postal_code: comp.postal_code || (comp as any).PostalCode,
                price_per_sqft: comp.price_per_sqft || 
                  (((comp as any).ClosePrice || comp.close_price) / ((comp as any).LivingArea || comp.living_area)) || undefined,
                days_on_market: (comp as any).days_on_market || 30 // Default fallback
              })) as ComparableProperty[];
              
              // Update comparables with mapped data
              comparables = mappedComparables;
              console.log('✅ [STEP 4] Mapped', comparables.length, 'comparables to frontend format');
              console.log('🔍 [STEP 4] Sample mapped comparable:', comparables[0]);
              
              const validPrices = comparables
                .map(comp => comp.close_price || comp.list_price)
                .filter((price): price is number => price != null && price > 0);
              
              const validSqftPrices = comparables
                .map(comp => comp.price_per_sqft)
                .filter((price): price is number => price != null && price > 0);
              
              const validDaysOnMarket = comparables
                .map(comp => comp.days_on_market)
                .filter((days): days is number => days != null && days > 0);

              marketContext = {
                avg_sale_price: validPrices.length > 0 ? validPrices.reduce((a, b) => a + b, 0) / validPrices.length : null,
                avg_price_per_sqft: validSqftPrices.length > 0 ? validSqftPrices.reduce((a, b) => a + b, 0) / validSqftPrices.length : null,
                avg_days_on_market: validDaysOnMarket.length > 0 ? validDaysOnMarket.reduce((a, b) => a + b, 0) / validDaysOnMarket.length : null,
                total_found: comparables.length
              };
              
              console.log('📈 [STEP 4] Calculated market context from mapped comparables:', marketContext);
              console.log('💰 [STEP 4] Valid prices found:', validPrices.length, 'Average:', marketContext.avg_sale_price);
              console.log('📏 [STEP 4] Valid $/sqft found:', validSqftPrices.length, 'Average:', marketContext.avg_price_per_sqft);
              fetchSuccess = true;
            }
          } else {
            console.warn('⚠️ [STEP 4] Failed to fetch report data - API response not successful');
            console.log('📊 [STEP 4] Response data:', response.data);
          }
        } catch (fetchError: any) {
          console.error('❌ [STEP 4] Error fetching report data:', fetchError);
          console.log('📊 [STEP 4] Error details:', fetchError.response?.data);
        }
      }

      console.log('🏘️ [STEP 4] Final comparables count:', comparables.length);
      console.log('📈 [STEP 4] Market context:', marketContext);
      console.log('✅ [STEP 4] Fetch success:', fetchSuccess);

      // Calculate basic metrics from available data with type safety
      const livingArea = parseInt(propertyData?.living_area || propertyData?.sqft || '2000');
      
      // ENHANCED: Use actual market data when available, intelligent fallback otherwise
      const avgPricePerSqft = marketContext.avg_price_per_sqft || 
                              (comparables.length > 0 ? 
                                comparables.reduce((sum, comp) => sum + (comp.price_per_sqft || 0), 0) / comparables.length :
                                (state.predictionResult?.prediction?.value || 400000) / livingArea);
      
      const recommendedPrice = state.predictionResult?.prediction?.value || (livingArea * avgPricePerSqft);

      // ENHANCED: Calculate market metrics from actual comparable data with proper fallbacks
      const comparablePrices = comparables
        .map(comp => comp.close_price || comp.list_price)
        .filter((price): price is number => price != null && price > 0);
      
      // FIXED: Direct mapping from API response (no initialization to 0)
      // Simple: Use the comparableCount from API or fallback to array length
      const finalComparableCount = reportData?.comparableCount || comparables?.length || 0;
      
      const actualMarketMetrics = {
        comparableCount: finalComparableCount,
        priceRange: comparablePrices.length > 0 ? {
          min: Math.min(...comparablePrices),
          max: Math.max(...comparablePrices),
          avg: Math.round(comparablePrices.reduce((a, b) => a + b, 0) / comparablePrices.length)
        } : {
          min: Math.round(recommendedPrice * 0.9),
          max: Math.round(recommendedPrice * 1.1),
          avg: recommendedPrice
        },
        daysOnMarket: Math.round(marketContext.avg_days_on_market || 
                                (comparables.length > 0 ? 
                                  comparables.reduce((sum, comp) => sum + ((comp as any).days_on_market || 30), 0) / comparables.length :
                                  30)),
        pricePerSqft: Math.round(avgPricePerSqft),
        marketVelocity: comparables.length > 15 ? 'Hot' : comparables.length > 8 ? 'Balanced' : 'Slow'
      };
      
      console.log('🎯 [STEP 4] === MARKET METRICS CREATED ===');
      console.log('📊 [STEP 4] actualMarketMetrics object:', actualMarketMetrics);
      console.log('🔢 [STEP 4] actualMarketMetrics.comparableCount:', actualMarketMetrics.comparableCount);
      console.log('🎯 [STEP 4] === MARKET METRICS END ===');

      // ENHANCED: Update insights to reflect actual data quality
      const dataQualityInsights = [
        `AI model predicts $${recommendedPrice.toLocaleString()} with ${Math.round((state.predictionResult?.prediction?.confidence_score || 0.85) * 100)}% confidence`,
        fetchSuccess ? 
          `Analysis based on ${finalComparableCount} comparable properties within 1-mile radius` :
          `Limited comparable data available - using enhanced ML prediction model`,
        `Market analysis shows ${actualMarketMetrics.marketVelocity.toLowerCase()} conditions with ${actualMarketMetrics.daysOnMarket} avg days on market`,
        `Property features align well with recent sales in the $${actualMarketMetrics.priceRange.min.toLocaleString()}-$${actualMarketMetrics.priceRange.max.toLocaleString()} range`
      ];

      // Calculate enhanced market insights
      const currentMonth = new Date().getMonth();
      const seasonalData = {
        'Spring': { adjustment: 0.05, months: ['Mar', 'Apr', 'May'] },
        'Summer': { adjustment: 0.08, months: ['Jun', 'Jul', 'Aug'] },
        'Fall': { adjustment: 0.02, months: ['Sep', 'Oct', 'Nov'] },
        'Winter': { adjustment: -0.03, months: ['Dec', 'Jan', 'Feb'] }
      };
      
      const currentSeason = currentMonth >= 2 && currentMonth <= 4 ? 'Spring' :
                           currentMonth >= 5 && currentMonth <= 7 ? 'Summer' :
                           currentMonth >= 8 && currentMonth <= 10 ? 'Fall' : 'Winter';

      const marketTemperatureScore = finalComparableCount > 15 ? 0.8 :
                                    finalComparableCount > 8 ? 0.6 :
                                    finalComparableCount > 3 ? 0.4 : 0.2;

      const marketInsights: MarketInsightsData = {
        marketTemperature: {
          score: marketTemperatureScore,
          label: marketTemperatureScore >= 0.8 ? 'Hot' :
                 marketTemperatureScore >= 0.6 ? 'Warm' :
                 marketTemperatureScore >= 0.4 ? 'Balanced' :
                 marketTemperatureScore >= 0.2 ? 'Cool' : 'Cold',
          indicators: {
            inventoryLevel: Math.max(1, Math.round(finalComparableCount * 0.7)),
            daysOnMarket: actualMarketMetrics.daysOnMarket,
            priceAppreciation: 0.05 + (marketTemperatureScore * 0.03),
            demandScore: marketTemperatureScore
          }
        },
        competitiveAnalysis: {
          activeListings: Math.round(finalComparableCount * 0.3),
          recentSales: finalComparableCount,
          priceCompetitiveness: recommendedPrice > actualMarketMetrics.priceRange.avg * 1.05 ? 'Above Market' :
                               recommendedPrice < actualMarketMetrics.priceRange.avg * 0.95 ? 'Below Market' : 'At Market',
          uniqueSellingPoints: [
            `${livingArea.toLocaleString()} sq ft of living space`,
            `${propertyData?.bedrooms || 4} bedrooms, ${propertyData?.bathrooms || 2} bathrooms`,
            `Built in ${propertyData?.year_built || 2000} - ${2025 - (propertyData?.year_built || 2000)} years old`,
            propertyData?.garage_spaces ? `${propertyData.garage_spaces} car garage` : 'Parking available',
            `Located in desirable ${propertyData?.city || 'neighborhood'} area`,
            fetchSuccess ? `Validated against ${finalComparableCount} recent comparable sales` : 'ML-enhanced pricing accuracy'
          ]
        },
        seasonalTrends: {
          currentSeason: currentSeason,
          seasonalAdjustment: seasonalData[currentSeason].adjustment,
          bestListingMonths: seasonalData['Spring'].months.concat(seasonalData['Summer'].months),
          marketVelocityTrend: marketTemperatureScore > 0.6 ? 'Increasing' : 
                              marketTemperatureScore > 0.3 ? 'Stable' : 'Decreasing'
        },
        riskFactors: {
          marketRisks: [
            ...(marketTemperatureScore < 0.4 ? ['Limited buyer demand in current market'] : []),
            ...(actualMarketMetrics.daysOnMarket > 45 ? ['Extended average days on market'] : []),
            ...(finalComparableCount < 5 ? ['Limited comparable sales data available'] : [])
          ],
          propertyRisks: [
            ...((propertyData?.year_built || 2000) < 1980 ? ['Older property may require updates'] : []),
            ...(livingArea < 1500 ? ['Below-average square footage for area'] : []),
            ...(!propertyData?.garage_spaces ? ['No garage may limit buyer appeal'] : [])
          ],
          timingRisks: [
            ...(currentSeason === 'Winter' ? ['Winter season typically slower for sales'] : []),
            ...(marketTemperatureScore < 0.4 ? ['Market conditions favor buyers over sellers'] : []),
            ...(recommendedPrice > actualMarketMetrics.priceRange.avg * 1.1 ? ['Aggressive pricing may extend time on market'] : [])
          ],
          overallRiskScore: Math.max(0.1, Math.min(0.9, 
            0.5 - (marketTemperatureScore * 0.3) + 
            (actualMarketMetrics.daysOnMarket > 45 ? 0.2 : 0) +
            (finalComparableCount < 5 ? 0.2 : 0)
          ))
        }
      };

      const enhancedData: EnhancedDashboardData = {
        executiveSummary: {
          recommendedPrice: recommendedPrice,
          confidence: (state.predictionResult?.prediction?.confidence_score || 0.85) * 100,
          marketPosition: 'market' as const,
          topInsights: dataQualityInsights,
          recommendedStrategy: fetchSuccess && finalComparableCount > 8 
            ? 'Strong comparable data supports confident pricing strategy with market positioning'
            : fetchSuccess && finalComparableCount > 3 
            ? 'Moderate comparable data suggests balanced pricing approach'
            : 'Enhanced ML prediction model provides reliable pricing guidance with conservative approach recommended'
        },
        marketMetrics: actualMarketMetrics,
        actionRecommendations: {
          pricing: {
            conservative: Math.round(recommendedPrice * 0.95),
            market: recommendedPrice,
            aggressive: Math.round(recommendedPrice * 1.05)
          },
          marketing: [
            `Highlight ${livingArea.toLocaleString()} sq ft living space as key differentiator`,
            `Emphasize ${propertyData?.year_built || 'quality'} construction and condition`,
            fetchSuccess ? 
              `Position competitively against ${finalComparableCount} recent comparable sales` :
              `Leverage ML-driven pricing insights for competitive market positioning`,
            `Target buyers seeking ${propertyData?.bedrooms || '4'} bedroom, ${propertyData?.bathrooms || '2'} bathroom properties`
          ],
          timeline: fetchSuccess && finalComparableCount > 8 ? 'Next 2-3 weeks' : 'Next 3-4 weeks',
          nextSteps: [
            'Professional photography and staging consultation',
            'Comprehensive listing preparation with MLS submission',
            'Multi-platform marketing campaign launch',
            'Strategic showing schedule and open house planning'
          ]
        },
        marketInsights: marketInsights,
        // Enhanced data for Claude - Include actual comparable data when available
        xgboostPrediction: state.predictionResult?.success && state.predictionResult?.prediction ? {
          predicted_price: state.predictionResult.prediction.value,
          confidence: state.predictionResult.prediction.confidence_score * 100,
          feature_importance: state.predictionResult.prediction.feature_importance?.map(f => ({
            feature: f.name,
            importance: f.importance,
            display_name: f.name.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
          })) || [],
          model_version: 'XGBoost-v2.1'
        } : undefined,
        // Use actual fetched comparable analysis when available
        comparableAnalysis: {
          comparables: comparables,
          marketContext: marketContext,
          searchMetrics: {
            totalFound: finalComparableCount,
            confidenceScore: state.predictionResult?.prediction?.confidence_score || 0.85,
            dataSource: fetchSuccess ? 'MLS Integration' : 'ML Prediction Model',
            searchRadius: 1.0,
            timeRange: '12 months',
            dataQuality: fetchSuccess ? 
              (finalComparableCount > 15 ? 'excellent' : finalComparableCount > 8 ? 'good' : 'limited') :
              'ml-enhanced'
          }
        }
      };

      setDashboardData(enhancedData);
      console.log('✅ [STEP 4] Dashboard data compiled successfully with', finalComparableCount, 'actual comparables (Final count:', finalComparableCount, ', Array length:', comparables.length, ')');
      
    } catch (error) {
      console.error('❌ [STEP 4] Error compiling dashboard data:', error);
    }
  }, [state]);

    // Separate effect for dashboard compilation (runs once)
  // Only runs if sessionStorage mount effect didn't already set data
  useEffect(() => {
    // Check if sessionStorage already has data (avoid overwriting with potentially incomplete compileDashboardData)
    const sellerData = sessionStorage.getItem('sellerReportComps') || sessionStorage.getItem('fullCMAResponse');
    if (!dashboardData && !sellerData) {
      console.log('🔄 [STEP 4] Initial dashboard data compilation triggered (no sessionStorage data)');
      compileDashboardData();
    } else if (!dashboardData && sellerData) {
      console.log('🔄 [STEP 4] Skipping compileDashboardData - sessionStorage mount effect will handle it');
    }
  }, [compileDashboardData, dashboardData]);

  // Load dashboard data from sessionStorage or API on mount
  useEffect(() => {
    console.log('🔄 [STEP 4] Component mounted - loading data');

    const buildDashboard = async () => {
    // Try sessionStorage first, then fetch from API
    let comps: any[] = [];
    let predictedPrice = 0;
    let mapeAccuracy = 89;
    let featureImportance: any[] = [];
    let subjectProperty: any = {};

    const sellerData = sessionStorage.getItem('sellerReportComps') || sessionStorage.getItem('fullCMAResponse');
    if (sellerData) {
      try {
        const parsed = JSON.parse(sellerData);
        comps = parsed.comparable_properties?.comparables || [];
        const prediction = parsed.ml_prediction || parsed.prediction || {};
        predictedPrice = prediction.predicted_price || prediction.value || 0;
        mapeAccuracy = prediction.mape_accuracy || (prediction.zip_mape ? 100 - prediction.zip_mape : 89);
        featureImportance = prediction.feature_importance || parsed.ml_prediction?.feature_importance || [];
        subjectProperty = state.propertyInput || parsed.subject_property || {};
        console.log('📊 [STEP 4] SessionStorage data: comps=', comps.length, 'predictedPrice=', predictedPrice);
      } catch (e) {
        console.warn('⚠️ [STEP 4] Failed to parse sessionStorage');
      }
    }

    // Always try to fetch from API to ensure we have complete data (predicted price + comps with classification)
    const currentReportId = sessionStorage.getItem('currentReportId') || '';
    if (currentReportId) {
      try {
        console.log('🔄 [STEP 4] Fetching from public CMA API for report:', currentReportId);
        const response = await axiosInstance.get(`/public/cma-report/${currentReportId}`);
        if (response.data.status === 'success' && response.data.report) {
          const report = response.data.report;
          // Always prefer API data - it's the source of truth
          if (report.valuation?.estimated_value) {
            predictedPrice = report.valuation.estimated_value;
          }
          if (report.comparables?.length > 0) {
            comps = report.comparables;
          }
          if (report.valuation?.feature_importance?.length > 0) {
            featureImportance = report.valuation.feature_importance;
          }
          if (report.property) {
            subjectProperty = report.property;
          }
          if (report.valuation?.confidence_score) {
            mapeAccuracy = Math.round(report.valuation.confidence_score * 100);
          }
          console.log('✅ [STEP 4] API data loaded: price=', predictedPrice, 'comps=', comps.length, 'features=', featureImportance.length);
        }
      } catch (apiErr) {
        console.warn('⚠️ [STEP 4] API fetch failed:', apiErr);
      }
    }

    if (predictedPrice && comps.length > 0) {
      try {
        const propertyData = state.propertyInput || subjectProperty || {};
        const livingArea = parseInt((propertyData as any).living_area || (propertyData as any).sqft || '1500');

        const prices = comps.map((c: any) => c.close_price || 0).filter((p: number) => p > 0);
        const avgPrice = prices.length ? Math.round(prices.reduce((a: number, b: number) => a + b, 0) / prices.length) : predictedPrice;
        const ppsf = comps.map((c: any) => c.price_per_sqft || (c.close_price && c.living_area ? c.close_price / c.living_area : 0)).filter((p: number) => p > 0);
        const avgPpsf = ppsf.length ? Math.round(ppsf.reduce((a: number, b: number) => a + b, 0) / ppsf.length) : Math.round(predictedPrice / livingArea);
        const doms = comps.map((c: any) => c.days_on_market || 0).filter((d: number) => d > 0);
        const avgDom = doms.length ? Math.round(doms.reduce((a: number, b: number) => a + b, 0) / doms.length) : 30;

        setDashboardData({
          executiveSummary: {
            recommendedPrice: predictedPrice,
            priceRange: {
              conservative: Math.round(predictedPrice * 0.95),
              market: predictedPrice,
              aggressive: Math.round(predictedPrice * 1.05)
            },
            confidence: mapeAccuracy,
            comparableCount: comps.length,
            marketPosition: 'Market Value',
            topInsights: [
              `AI model predicts $${predictedPrice.toLocaleString()} with ${mapeAccuracy}% accuracy`,
              `Based on ${comps.length} comparable properties in the area`,
              `Average days on market: ${avgDom}`,
              `Market price per sqft: $${avgPpsf}`
            ],
            recommendedStrategy: `Price at $${predictedPrice.toLocaleString()} to attract qualified buyers based on ${comps.length} recent comparable sales.`
          },
          marketMetrics: {
            comparableCount: comps.length,
            priceRange: {
              min: prices.length ? Math.min(...prices) : Math.round(predictedPrice * 0.9),
              max: prices.length ? Math.max(...prices) : Math.round(predictedPrice * 1.1),
              avg: avgPrice
            },
            daysOnMarket: avgDom,
            pricePerSqft: avgPpsf,
            marketVelocity: comps.length > 15 ? 'Hot' : comps.length > 8 ? 'Balanced' : 'Slow'
          },
          comparableAnalysis: {
            comparables: comps.slice(0, 10),
            totalCount: comps.length,
            avgPrice: avgPrice,
            avgPricePerSqft: avgPpsf,
            avgDaysOnMarket: avgDom
          },
          xgboostPrediction: {
            predicted_price: predictedPrice,
            confidence: mapeAccuracy,
            feature_importance: featureImportance.length > 0 ? featureImportance : [
              { name: 'living_area', display_name: 'Living Area', importance_score: 0.35, property_value: `${subjectProperty?.living_area || 0} sqft` },
              { name: 'location', display_name: 'Location', importance_score: 0.25, property_value: subjectProperty?.postal_code || 'Local Market' },
              { name: 'bedrooms', display_name: 'Bedrooms', importance_score: 0.20, property_value: `${subjectProperty?.bedrooms || 0} beds` },
              { name: 'bathrooms', display_name: 'Bathrooms', importance_score: 0.15, property_value: `${subjectProperty?.bathrooms || 0} baths` },
              { name: 'year_built', display_name: 'Year Built', importance_score: 0.05, property_value: `${subjectProperty?.year_built || 'N/A'}` }
            ]
          },
          actionRecommendations: {
            pricing: {
              conservative: Math.round(predictedPrice * 0.95),
              market: predictedPrice,
              aggressive: Math.round(predictedPrice * 1.05)
            },
            marketing: [
              `Highlight ${comps.filter((c: any) => c.classification === 'renovated').length > comps.length / 2 ? 'renovated condition' : 'value opportunity'} in listing`,
              `Price competitively against ${comps.length} recent comparable sales`,
              `Target buyers searching in the $${Math.round(predictedPrice * 0.9 / 1000)}K-$${Math.round(predictedPrice * 1.1 / 1000)}K range`,
              'Professional photography and virtual tour recommended'
            ],
            nextSteps: [
              'Review comparable properties with seller',
              'Discuss pricing strategy and timeline',
              'Schedule professional photography',
              'Prepare listing description and marketing materials',
              'Set showing schedule and open house dates'
            ],
            timeline: '2-4 weeks to listing'
          },
          marketInsights: null
        } as any);

        console.log('✅ [STEP 4] Dashboard loaded from sessionStorage');

        // Generate AI insights - check cache first, only call AI if not cached
        (async () => {
          try {
            // Check for cached insights - first DB (via public report), then sessionStorage
            const currentReportId = sessionStorage.getItem('currentReportId') || '';

            // Try to get from the public report (includes claude_insights if saved)
            try {
              const reportResp = await axiosInstance.get(`/public/cma-report/${currentReportId}`);
              if (reportResp.data?.report?.claude_insights) {
                setClaudeInsights(reportResp.data.report.claude_insights);
                console.log('✅ [STEP 4] Using DB cached AI insights (no tokens spent)');
                setIsLoadingInsights(false);
                return;
              }
            } catch (e) {}

            // Try sessionStorage cache
            const cachedInsights = sessionStorage.getItem(`aiInsights_${currentReportId}`);
            if (cachedInsights) {
              try {
                const cached = JSON.parse(cachedInsights);
                setClaudeInsights(cached);
                console.log('✅ [STEP 4] Using session cached AI insights (no tokens spent)');
                setIsLoadingInsights(false);
                return;
              } catch (e) {}
            }

            setIsLoadingInsights(true);
            const compSummary = comps.slice(0, 10).map((c: any, i: number) =>
              `${c.address}: $${(c.close_price || 0).toLocaleString()}, ${c.living_area}sqft, ${c.classification === 'renovated' ? 'Renovated' : 'Original'}, ${c.days_on_market || 0} DOM`
            ).join('; ');

            const insightResponse = await axiosInstance.post('/public/ai/chat', {
              messages: [
                { role: 'system', content: `You are a real estate CMA analyst. Property: ${(subjectProperty as any)?.address || 'Subject property'}. AI Estimated Value: $${predictedPrice.toLocaleString()} (${mapeAccuracy}% accuracy). ${comps.length} comps found. Avg price: $${avgPrice.toLocaleString()}. Avg $/sqft: $${avgPpsf}. Avg DOM: ${avgDom}. Top comps: ${compSummary}` },
                { role: 'user', content: 'Give me a brief market analysis with: 1) A one-paragraph market narrative referencing specific comps, 2) A pricing recommendation with reasoning based on the comps, 3) Three key selling points, 4) Two potential risks or challenges. Be specific - reference actual comp addresses and prices.' }
              ],
              sessionId: `insights_${Date.now()}`,
              context: { type: 'seller_cma_insights' }
            });

            const insightText = insightResponse.data.response || insightResponse.data.message || '';
            if (insightText) {
              const renovatedCount = comps.filter((c: any) => c.classification === 'renovated').length;
              const originalCount = comps.length - renovatedCount;

              const insightsData = {
                status: 'complete',
                summary: insightText,
                marketNarrative: insightText,
                pricingRecommendation: `Based on ${comps.length} comparable sales with ${mapeAccuracy}% AI prediction accuracy, the recommended list price is $${predictedPrice.toLocaleString()}. The market median is $${avgPrice.toLocaleString()} with an average of $${avgPpsf}/sqft.`,
                marketPositioning: `This property is positioned at $${predictedPrice.toLocaleString()} in a market where ${renovatedCount} of ${comps.length} recent comps were renovated properties. Average days on market is ${avgDom}. Properties in this range are seeing ${avgDom < 30 ? 'strong' : avgDom < 60 ? 'moderate' : 'slower'} buyer demand.`,
                agentStrategy: `Focus on the property's value proposition at $${predictedPrice.toLocaleString()}. With ${originalCount} original-condition comps averaging lower prices, highlight any updates or unique features. Consider staging and professional photography to maximize appeal. Price competitively to attract multiple offers within the first 2 weeks.`,
                timingInsights: `Current market conditions show ${avgDom} average days on market. ${new Date().getMonth() >= 2 && new Date().getMonth() <= 7 ? 'Spring/Summer is typically the strongest selling season in Arizona.' : 'Fall/Winter may see slightly less competition, which can benefit well-priced listings.'} List within 2-4 weeks to capture current market momentum.`,
                confidence: mapeAccuracy,
                generatedAt: new Date().toISOString()
              };

              setClaudeInsights(insightsData as any);

              // Save to sessionStorage
              sessionStorage.setItem(`aiInsights_${currentReportId}`, JSON.stringify(insightsData));

              // Save to database (persists across sessions)
              if (currentReportId) {
                axiosInstance.post(`/public/save-insights/${currentReportId}`, { insights: insightsData }).catch(() => {});
                console.log('💾 [STEP 4] AI insights saved to DB for report:', currentReportId);
              }
            }
          } catch (err) {
            console.warn('⚠️ [STEP 4] AI insights generation failed:', err);
          } finally {
            setIsLoadingInsights(false);
          }
        })();

      } catch (e) {
        console.error('❌ [STEP 4] Failed to build dashboard:', e);
        compileDashboardData(); // Fallback
      }
    } else {
      console.log('⚠️ [STEP 4] No predicted price or comps available, using compileDashboardData fallback');
      compileDashboardData();
    }
    }; // end buildDashboard

    buildDashboard();
  }, []); // Run only on mount

  // Manual regenerate function with reset - MOVED UP
  const handleRegenerateInsights = useCallback(() => {
    console.log('🔄 [CLAUDE INSIGHTS] Manual regeneration requested');
    setInsightsRequested(false);
    setClaudeInsights(null);
    generateClaudeInsights();
  }, [generateClaudeInsights]);

  // Polling mechanism - disabled for seller reports (insights saved to DB instead)
  const startClaudePolling = useCallback(() => {
    // Skip polling for seller reports - insights are generated and saved on Step 4 mount
    const sellerCheck = sessionStorage.getItem('sellerReportComps');
    if (sellerCheck) {
      console.log('✅ [POLLING] Skipped - seller report uses DB cached insights');
      return;
    }

    let pollAttempts = 0;
    const maxAttempts = 12;

    const pollInterval = setInterval(async () => {
      pollAttempts++;

      try {
        const currentReportId = sessionStorage.getItem('currentReportId');
        const response = await axiosInstance.get(`/smart-cma/insights-status/${currentReportId}`);
        
        // Axios automatically handles HTTP errors, so if we get here, the request was successful
        const result = response.data;
        
        if (result.status === 'completed' && result.success) {
          console.log('✅ [STEP 4 POLLING] Claude insights completed!');
          console.log('🔍 [STEP 4 POLLING] Available properties:', Object.keys(result));
          
          // The insights are directly on the result object (spread by server)
          const insights = {
            marketNarrative: result.marketNarrative || '',
            pricingRecommendation: result.pricingRecommendation || '',
            marketPositioning: result.marketPositioning || '',
            clientConsiderations: result.clientConsiderations || '',
            agentStrategy: result.agentStrategy || '',
            riskAssessment: result.riskAssessment || '',
            competitiveAdvantage: result.competitiveAdvantage || '',
            timingInsights: result.timingInsights || '',
            negotiationPoints: result.negotiationPoints || '',
            marketTrends: result.marketTrends || '',
            confidence: result.confidence || 85,
            generatedAt: result.generatedAt || new Date().toISOString()
          };
          
          console.log('✅ [STEP 4 POLLING] Extracted insights:', Object.keys(insights));
          console.log('📝 [STEP 4 POLLING] Sample narrative:', insights.marketNarrative?.substring(0, 100) + '...');
          
          setClaudeInsights(insights);
          setIsLoadingInsights(false);
          clearInterval(pollInterval);
          return;
        } else if (result.status === 'failed' || !result.success) {
          console.log('❌ [STEP 4 POLLING] Claude insights failed');
          setIsLoadingInsights(false);
          clearInterval(pollInterval);
          return;
        }
        
        if (pollAttempts >= maxAttempts) {
          console.log('⏰ [STEP 4 POLLING] Polling timeout, stopping');
          setIsLoadingInsights(false);
          clearInterval(pollInterval);
        } else {
          console.log(`🔄 [STEP 4 POLLING] Still processing... (${pollAttempts}/${maxAttempts})`);
        }
        
      } catch (error) {
        console.log(`🔄 [STEP 4 POLLING] Poll attempt ${pollAttempts} failed, continuing...`);
      }
    }, 10000); // Poll every 10 seconds
  }, []);

          // NEW: Enhanced check that auto-generates if needed - NOW USES PROPER DEPENDENCIES
  const checkClaudeInsightsStatus = useCallback(async () => {
    // Skip for seller reports - insights handled on mount
    const sellerCheck = sessionStorage.getItem('sellerReportComps');
    if (sellerCheck) {
      return;
    }
    setIsLoadingInsights(true);
    
    try {
      const currentReportId = sessionStorage.getItem('currentReportId');
      if (!currentReportId) {
        console.warn('🧠 [STEP 4] No report ID found for Claude insights');
        setIsLoadingInsights(false);
        return;
      }

      console.log('🔍 [STEP 4] Checking Claude insights status for report:', currentReportId);
      const response = await axiosInstance.get(`/smart-cma/insights-status/${currentReportId}`);
      
      const result = response.data;
      console.log('📊 [STEP 4] Insights status result:', result.status);
      
      if (result.status === 'completed' && result.success) {
        console.log('✅ [STEP 4] Claude insights ready!');
        console.log('🔍 [STEP 4] Available properties:', Object.keys(result));
        
        // The insights are directly on the result object (spread by server)
        const insights = {
          marketNarrative: result.marketNarrative || '',
          pricingRecommendation: result.pricingRecommendation || '',
          marketPositioning: result.marketPositioning || '',
          clientConsiderations: result.clientConsiderations || '',
          agentStrategy: result.agentStrategy || '',
          riskAssessment: result.riskAssessment || '',
          competitiveAdvantage: result.competitiveAdvantage || '',
          timingInsights: result.timingInsights || '',
          negotiationPoints: result.negotiationPoints || '',
          marketTrends: result.marketTrends || '',
          confidence: result.confidence || 85,
          generatedAt: result.generatedAt || new Date().toISOString()
        };
        
        console.log('✅ [STEP 4] Extracted insights:', Object.keys(insights));
        console.log('📝 [STEP 4] Sample narrative:', insights.marketNarrative?.substring(0, 100) + '...');
        
        setClaudeInsights(insights);
        setIsLoadingInsights(false);
      } else if (result.status === 'processing') {
        console.log('🔄 [STEP 4] Claude insights still processing, starting polling...');
        startClaudePolling();
      } else if (result.status === 'failed') {
        console.log('❌ [STEP 4] Claude insights failed, will auto-retry...');
        // Auto-retry failed insights
        setTimeout(() => {
          handleRegenerateInsights();
        }, 2000);
      } else {
        // No insights found - auto-generate them
        console.log('🚀 [STEP 4] No insights found, auto-generating...');
        await handleRegenerateInsights();
      }
      
    } catch (error) {
      console.warn('⚠️ [STEP 4] Error checking Claude status:', error);
      // If status check fails, try to generate insights anyway
      console.log('🚀 [STEP 4] Status check failed, attempting to generate insights...');
      setTimeout(() => {
        handleRegenerateInsights();
      }, 1000);
    }
  }, []);

  // Separate effect for Claude insights - ENHANCED HYBRID APPROACH
  useEffect(() => {
    // Start checking/generating Claude insights when dashboard is ready
    if (dashboardData && !claudeInsights && !isLoadingInsights) {
      console.log('🧠 [STEP 4] Dashboard ready, checking Claude insights status...');
      checkClaudeInsightsStatus();
    }
  }, [dashboardData, claudeInsights, isLoadingInsights, checkClaudeInsightsStatus]);

  const handleExportReport = (format: string) => {
    console.log(`Exporting report in ${format} format`);
    // Implementation for different export formats
    switch (format) {
      case 'pdf':
        // Generate PDF report
        alert('PDF report generation would be implemented here');
        break;
      case 'email':
        // Email to client
        alert('Email delivery would be implemented here');
        break;
      case 'print':
        // Print report
        window.print();
        break;
      case 'share':
        // Generate shareable link
        alert('Shareable link generation would be implemented here');
        break;
    }
  };

  // FIXED: Remove argument from goToPreviousStep
  const handleBackToAnalysis = () => {
    goToPreviousStep();
  };

  const togglePresentationMode = () => {
    setPresentationMode(!presentationMode);
  };

  if (!dashboardData) {
    return (
      <div className="text-center py-5">
        <div className="spinner-border text-primary mb-3" role="status">
          <span className="visually-hidden">Loading dashboard...</span>
        </div>
        <p className="text-muted">Compiling comprehensive market analysis...</p>
      </div>
    );
  }

  return (
    <div className={`step4-dashboard ${presentationMode ? 'presentation-mode' : ''}`}>
      {/* Enhanced Header with AI Status */}
      <div className="dashboard-header mb-4">
        <div className="d-flex justify-content-between align-items-center">
          <div>
            <h1 className="mb-2">
              <FontAwesomeIcon icon={faTachometerAlt} className="me-3 text-primary" />
              Smart CMA Dashboard
            </h1>
            <p className="text-muted mb-0">
              Comprehensive market analysis powered by XGBoost ML and Claude AI
            </p>
          </div>
          <div className="dashboard-controls">
            <button
              className="btn btn-outline-secondary me-2"
              onClick={togglePresentationMode}
            >
              <FontAwesomeIcon icon={presentationMode ? faEyeSlash : faEye} className="me-2" />
              {presentationMode ? 'Exit' : 'Present'}
            </button>
            <button className="btn btn-outline-primary me-2" onClick={() => handleExportReport('pdf')}>
              <FontAwesomeIcon icon={faDownload} className="me-2" />
              Export PDF
            </button>
            <button className="btn btn-outline-info" onClick={handleBackToAnalysis}>
              <FontAwesomeIcon icon={faArrowLeft} className="me-2" />
              Back to Analysis
            </button>
          </div>
        </div>
      </div>

      {/* Claude AI Insights Panel */}
      <ClaudeInsightsPanel 
        insights={claudeInsights}
        isLoading={isLoadingInsights}
        onRegenerate={handleRegenerateInsights}
        dashboardData={dashboardData}
      />

      {/* AI Chat Panel */}
      {dashboardData && (
        <AIChatPanel
          reportData={dashboardData}
          reportId={sessionStorage.getItem('currentReportId') || ''}
          isExpanded={isChatExpanded}
          onToggle={() => setIsChatExpanded(!isChatExpanded)}
        />
      )}

      {/* Enhanced Executive Summary */}
      <EnhancedExecutiveSummary 
        summary={dashboardData.executiveSummary}
        xgboostPrediction={dashboardData.xgboostPrediction}
        claudeInsights={claudeInsights}
      />

      {/* Enhanced Market Insights Dashboard */}
      {dashboardData.marketInsights && (
        <MarketInsightsDashboard 
          insights={dashboardData.marketInsights}
          propertyPrice={dashboardData.executiveSummary.recommendedPrice}
        />
      )}

      {/* Lead Assignment & Report Sharing - Coming Soon */}
      {/* TODO: Implement Lead Assignment and Enhanced Report Sharing components */}

      {/* Existing components continue... */}
      
      {state.propertyInput && (
        <PropertyDetailsSummary property={state.propertyInput} />
      )}

      <div className="row">
        <div className="col-lg-8">
          {dashboardData.xgboostPrediction && dashboardData.xgboostPrediction.predicted_price ? (
            <SmartCMACharts
              predictionData={{
                value: dashboardData.xgboostPrediction.predicted_price,
                confidence_score: dashboardData.xgboostPrediction.confidence / 100
              }}
              marketData={{
                feature_importance: (dashboardData.xgboostPrediction.feature_importance || []).map((f: any) => ({
                  name: f.display_name || f.name || f.feature,
                  importance: f.importance_score || f.importance || 0,
                  value: f.property_value || f.feature || f.name
                })),
                price_trends: {
                  months: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun'],
                  median_prices: dashboardData.comparableAnalysis?.comparables ?
                    dashboardData.comparableAnalysis.comparables
                      .slice(0, 6)
                      .map((comp: any) => comp.close_price || comp.list_price || 400000)
                      .sort((a: number, b: number) => a - b) :
                    [400000, 405000, 410000, 415000, 420000, 425000]
                },
                price_distribution: {
                  price_ranges: (() => {
                    const comps = dashboardData.comparableAnalysis?.comparables || [];
                    const prices = comps.map((c: any) => c.close_price || c.list_price).filter((p: any) => p > 0);
                    if (prices.length === 0) return ['<$300k', '$300-400k', '$400-500k', '$500-600k'];
                    const min = Math.min(...prices); const max = Math.max(...prices); const range = (max - min) / 4;
                    return [`$${Math.round(min/1000)}k-${Math.round((min+range)/1000)}k`, `$${Math.round((min+range)/1000)}k-${Math.round((min+2*range)/1000)}k`, `$${Math.round((min+2*range)/1000)}k-${Math.round((min+3*range)/1000)}k`, `$${Math.round((min+3*range)/1000)}k-${Math.round(max/1000)}k`];
                  })(),
                  counts: (() => {
                    const comps = dashboardData.comparableAnalysis?.comparables || [];
                    const prices = comps.map((c: any) => c.close_price || c.list_price).filter((p: any) => p > 0);
                    if (prices.length === 0) return [2, 8, 15, 3];
                    const min = Math.min(...prices); const max = Math.max(...prices); const range = (max - min) / 4;
                    return [prices.filter((p: number) => p < min+range).length, prices.filter((p: number) => p >= min+range && p < min+2*range).length, prices.filter((p: number) => p >= min+2*range && p < min+3*range).length, prices.filter((p: number) => p >= min+3*range).length];
                  })()
                },
                days_on_market: (() => {
                  const comps = dashboardData.comparableAnalysis?.comparables || [];
                  const doms = comps.map((c: any) => c.days_on_market || 0).filter((d: number) => d > 0);
                  return {
                    ranges: ['0-30 days', '31-60 days', '61-90 days', '90+ days'],
                    counts: [
                      doms.filter((d: number) => d <= 30).length,
                      doms.filter((d: number) => d > 30 && d <= 60).length,
                      doms.filter((d: number) => d > 60 && d <= 90).length,
                      doms.filter((d: number) => d > 90).length
                    ]
                  };
                })(),
                renovated_vs_original: (() => {
                  const comps = dashboardData.comparableAnalysis?.comparables || [];
                  const renovated = comps.filter((c: any) => c.classification === 'renovated');
                  const original = comps.filter((c: any) => c.classification !== 'renovated');
                  const avg = (arr: any[], field: string) => arr.length ? Math.round(arr.reduce((s: number, c: any) => s + (c[field] || 0), 0) / arr.length) : 0;
                  return {
                    categories: ['Renovated', 'Original'],
                    avgPrices: [avg(renovated, 'close_price'), avg(original, 'close_price')],
                    avgPpsf: [avg(renovated, 'price_per_sqft'), avg(original, 'price_per_sqft')],
                    counts: [renovated.length, original.length]
                  };
                })()
              }}
            />
          ) : (
            <div className="card h-100">
              <div className="card-body d-flex align-items-center justify-content-center">
                <p className="text-muted">Loading market analysis...</p>
              </div>
            </div>
          )}
        </div>
        <div className="col-lg-4">
          <AgentActionCenter actions={dashboardData.actionRecommendations} />
          <ReportExportPanel
            onExport={handleExportReport}
            portalLink={(() => {
              const reportId = sessionStorage.getItem('currentReportId');
              if (!reportId) return undefined;
              let agentId = '';
              try {
                const token = localStorage.getItem('auth_token') || localStorage.getItem('agent_token') || '';
                if (token) agentId = JSON.parse(atob(token.split('.')[1])).id || '';
              } catch (e) {}
              const host = window.location.hostname;
              const base = host.includes('dev.') || host === 'localhost' ? 'https://dev.aisearchportal.com' : 'https://aisearchportal.com';
              return `${base}/cma-report/${reportId}${agentId ? `?agentId=${agentId}` : ''}`;
            })()}
          />
        </div>
      </div>
    </div>
  );
};

export default Step4Dashboard; 